import React, { useState, ChangeEvent, FormEvent } from 'react';

interface ContactFormData {
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  message: string;
}

const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState<ContactFormData>({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    message: ''
  });

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    console.log('Contact form submitted:', formData);
    alert('Thank you! We will get back to you within one business day.');
  };

  return (
    <>
      <section className="uc-hero">
        <div className="uc-hero-inner">
          <span className="uc-hero-label">Contact Us</span>
          <h1>We'd love to<br/><em>hear from you.</em></h1>
          <p>Whether you have a question about a simulation, want to discuss a custom project, or just want to say hello — we're here.</p>
        </div>
      </section>

      <div className="uc-section">
        <div className="uc-section-inner">
          <div className="contact-layout">
            <div>
              <div className="section-label"><span>Get in touch</span></div>
              <h2 className="section-title" style={{fontSize:'32px'}}>Let's talk about<br/><em>your expansion.</em></h2>
              <p className="section-sub" style={{marginBottom:'40px'}}>Fill in the form and we'll get back to you within one business day. Or reach us directly using the details below.</p>
              <div style={{display:'flex',flexDirection:'column',gap:'24px'}}>
                <div style={{display:'flex',alignItems:'center',gap:'16px'}}>
                  <div style={{width:'44px',height:'44px',borderRadius:'10px',background:'var(--cream-dark)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="var(--forest)" strokeWidth="1.8" strokeLinecap="round" width="18" height="18"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                  </div>
                  <div>
                    <p style={{fontSize:'11px',letterSpacing:'0.1em',textTransform:'uppercase' as const,color:'var(--text-light)',marginBottom:'4px'}}>Email</p>
                    <a href="mailto:hello@marketentry.io" style={{fontSize:'15px',color:'var(--forest)',fontWeight:500,textDecoration:'none'}}>hello@marketentry.io</a>
                  </div>
                </div>
                <div style={{display:'flex',alignItems:'center',gap:'16px'}}>
                  <div style={{width:'44px',height:'44px',borderRadius:'10px',background:'var(--cream-dark)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="var(--forest)" strokeWidth="1.8" strokeLinecap="round" width="18" height="18"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                  </div>
                  <div>
                    <p style={{fontSize:'11px',letterSpacing:'0.1em',textTransform:'uppercase' as const,color:'var(--text-light)',marginBottom:'4px'}}>Phone</p>
                    <a href="tel:+5511976963091" style={{fontSize:'15px',color:'var(--forest)',fontWeight:500,textDecoration:'none'}}>+55 11 97 696 30 91</a>
                  </div>
                </div>
                <div style={{display:'flex',alignItems:'center',gap:'16px'}}>
                  <div style={{width:'44px',height:'44px',borderRadius:'10px',background:'var(--cream-dark)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="var(--forest)" strokeWidth="1.8" strokeLinecap="round" width="18" height="18"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                  </div>
                  <div>
                    <p style={{fontSize:'11px',letterSpacing:'0.1em',textTransform:'uppercase' as const,color:'var(--text-light)',marginBottom:'4px'}}>Response time</p>
                    <p style={{fontSize:'15px',color:'var(--forest)',fontWeight:500,margin:0}}>Within 1 business day</p>
                  </div>
                </div>
              </div>
            </div>
            <div style={{background:'var(--white)',borderRadius:'20px',padding:'36px',boxShadow:'0 8px 32px rgba(0,0,0,0.07)',border:'1px solid rgba(26,46,31,0.06)'}}>
              <h4 style={{fontFamily:'Libre Baskerville,serif',fontSize:'20px',color:'var(--forest)',marginBottom:'6px'}}>Send us a message</h4>
              <p style={{fontSize:'13px',color:'var(--text-light)',marginBottom:'24px',lineHeight:'1.5'}}>We'll get back to you within one business day.</p>
              <form onSubmit={handleSubmit}>
                <div className="contact-name-row">
                  <div className="form-group"><label>First Name *</label><input type="text" name="firstName" value={formData.firstName} onChange={handleChange} placeholder="e.g., Sarah" required /></div>
                  <div className="form-group"><label>Last Name *</label><input type="text" name="lastName" value={formData.lastName} onChange={handleChange} placeholder="e.g., Johnson" required /></div>
                </div>
                <div className="form-group" style={{marginBottom:'16px'}}><label>Work Email *</label><input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="e.g., you@company.com" required /></div>
                <div className="form-group" style={{marginBottom:'16px'}}><label>Company</label><input type="text" name="company" value={formData.company} onChange={handleChange} placeholder="e.g., Acme Corp" /></div>
                <div className="form-group" style={{marginBottom:'16px'}}>
                  <label>Message *</label>
                  <textarea name="message" value={formData.message} onChange={handleChange} rows={4} placeholder="Tell us what you're working on..." style={{width:'100%',padding:'12px 14px',border:'1.5px solid #ddd8ce',borderRadius:'10px',fontFamily:'DM Sans,sans-serif',fontSize:'14px',color:'var(--forest)',background:'var(--cream)',outline:'none',resize:'vertical',lineHeight:'1.6',boxSizing:'border-box'}} required></textarea>
                </div>
                <button type="submit" className="form-submit">Send Message →</button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ContactPage;
